import type {
  IntakesListResponse,
  IntakeDetail,
  FieldMappingResponse,
  APIResponse,
} from "@/types/api";

const INTAKE_API_URL = import.meta.env.DEV
  ? "http://localhost:8787"
  : "https://rh-kb-api.haus-bots.workers.dev";

class IntakeAPIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "IntakeAPIError";
  }
}

// Helper function to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  console.log("🔄 handleResponse (Intake API)", {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    url: response.url
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    let errorData = null;

    try {
      errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
      console.error("❌ Intake API Error Response:", errorData);
    } catch (parseError) {
      console.error("❌ Failed to parse error response:", parseError);
    }

    throw new IntakeAPIError(response.status, errorMessage);
  }

  const data = await response.json();
  console.log("✅ Intake API Success Response:", data);
  return data;
}

// Helper function to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem("auth_token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  // Dev-only bypass header
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_SECRET) {
    headers["X-Dev-Bypass"] = import.meta.env.VITE_DEV_BYPASS_SECRET;
  }

  return headers;
}

export interface IntakeSearchFilters {
  page?: number;
  pageSize?: number;
}

export const intakes = {
  // Get paginated list of intakes
  async list(filters?: IntakeSearchFilters): Promise<IntakesListResponse> {
    console.log("📋 intakes.list called with filters:", filters);

    const url = new URL(`${INTAKE_API_URL}/api/intakes`);

    if (filters) {
      if (filters.page) url.searchParams.set("page", filters.page.toString());
      if (filters.pageSize) url.searchParams.set("pageSize", filters.pageSize.toString());
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return handleResponse<IntakesListResponse>(response);
  },

  // Get detailed intake by ID
  async getById(recordId: string): Promise<IntakeDetail> {
    console.log("📄 intakes.getById called with recordId:", recordId);

    const response = await fetch(`${INTAKE_API_URL}/api/intakes/${recordId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return handleResponse<IntakeDetail>(response);
  },

  // Get field mapping (admin only)
  async getFieldMapping(): Promise<FieldMappingResponse> {
    console.log("🗺️ intakes.getFieldMapping called");

    const response = await fetch(`${INTAKE_API_URL}/api/intakes/fieldmap`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return handleResponse<FieldMappingResponse>(response);
  },

  // Webhook for cache invalidation (called by Airtable automations)
  async webhook(data: { recordId: string; agentEmail?: string }, secret: string): Promise<{ ok: boolean; invalidated: number }> {
    console.log("🪝 intakes.webhook called with data:", data);

    const response = await fetch(`${INTAKE_API_URL}/api/intakes/webhook`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Webhook-Secret": secret,
      },
      body: JSON.stringify(data),
    });

    return handleResponse<{ ok: boolean; invalidated: number }>(response);
  }
};